import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Card, CardContent, Button, Input } from '../components';
import { OrganisationSelector } from '../components/OrganisationSelector';
import { ImageGallery } from '../components/ImagePreview';
import { useAppDispatch } from '../store/hooks';
import { addNotification } from '../store/slices/uiSlice';
import { courrierService, organisationService, piecesService } from '../services/api';
import { queryKeys } from '../lib/react-query';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { CreateCourrierRequest, Organisation, OrganisationDefaults } from '../types/api';
import { CameraCaptureOpenCV } from '../components/CameraCaptureOpenCV';
import { FileUpload } from '../components/FileUpload';
import { fileService } from '../services/api';

const isDev =
  typeof import.meta !== 'undefined' &&
  typeof (import.meta as any).env !== 'undefined' &&
  ((import.meta as any).env.MODE !== 'production');

// Détection du type d'appareil
const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
};

// Fonction utilitaire pour formater une date au format datetime-local
const formatDateTimeForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export function CourrierCreate() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();

  const [formData, setFormData] = useState<CreateCourrierRequest>({
    reference: '',
    type: 'ENTRANT',
    sender: '',
    recipient: '',
    subject: '',
    priority: 'NORMAL',
    dueDate: undefined,
    metadata: {},
  });

  const [errors, setErrors] = useState<Partial<CreateCourrierRequest>>({});
  const [attachedFiles, setAttachedFiles] = useState<Array<any>>([]); // items like { id: string, file: File, url: string } (fallback: raw File)
  const [isScanning, setIsScanning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isCaptureProcessing, setIsCaptureProcessing] = useState(false);
  const [detectorAvailable, setDetectorAvailable] = useState(true);
  const [detectorLoading, setDetectorLoading] = useState(false);

  // Détection du type d'appareil au chargement
  useEffect(() => {
    setIsMobile(isMobileDevice());

    // Écouter les changements de taille de fenêtre
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Détecteur natif toujours disponible
  useEffect(() => {
    setDetectorAvailable(true);
    setDetectorLoading(false);
  }, []);

  // Charger les organisations disponibles
  const { data: organisations = [] } = useQuery({
    queryKey: queryKeys.organisations.lists(),
    queryFn: () => organisationService.getOrganisations(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Charger les préférences utilisateur par défaut (avec gestion d'erreur silencieuse)
  const { data: userDefaults, error: userDefaultsError } = useQuery({
    queryKey: queryKeys.userDefaults.get(),
    queryFn: () => courrierService.getUserDefaults(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      // Ne pas réessayer si c'est une erreur d'authentification
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return false;
      }
      // Réessayer jusqu'à 2 fois pour les autres erreurs
      return failureCount < 2;
    },
    retryDelay: 1000,
  });

  // Appliquer les valeurs par défaut lors du chargement
  useEffect(() => {
    if (userDefaults && organisations.length > 0) {
      setFormData(prev => ({
        ...prev,
        sender: userDefaults.defaultSenderId
          ? organisations.find(org => org.id === userDefaults.defaultSenderId)?.displayName || ''
          : prev.sender,
        recipient: userDefaults.defaultRecipientId
          ? organisations.find(org => org.id === userDefaults.defaultRecipientId)?.displayName || ''
          : prev.recipient,
      }));
    }
  }, [userDefaults, organisations]);

  // Pré-remplir automatiquement l'organisation de l'utilisateur selon le type de courrier
  useEffect(() => {
    if (!user?.organisationId || organisations.length === 0) return;
    const userOrg = organisations.find(org => org.id === user.organisationId);
    if (!userOrg) return;

    if (formData.type === 'ENTRANT') {
      // Entrant: destinataire = organisation de l'utilisateur (si vide)
      setFormData(prev => ({
        ...prev,
        recipient: prev.recipient && prev.recipient.trim().length > 0 ? prev.recipient : userOrg.displayName,
      }));
    } else if (formData.type === 'SORTANT') {
      // Sortant: expéditeur = organisation de l'utilisateur (si vide)
      setFormData(prev => ({
        ...prev,
        sender: prev.sender && prev.sender.trim().length > 0 ? prev.sender : userOrg.displayName,
      }));
    }
  }, [user?.organisationId, organisations, formData.type]);

  // Mutation pour créer un courrier
  const createMutation = useMutation({
    mutationFn: (data: CreateCourrierRequest) => courrierService.createCourrier(data),
  });

  const validateForm = (): boolean => {
    const newErrors: Partial<CreateCourrierRequest> = {};

    if (!formData.sender?.trim()) {
      newErrors.sender = 'L\'expéditeur est requis';
    }

    if (!formData.recipient?.trim()) {
      newErrors.recipient = 'Le destinataire est requis';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Le sujet est requis';
    } else if (formData.subject.length < 5) {
      newErrors.subject = 'Le sujet doit contenir au moins 5 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Empêcher la soumission si une capture est en cours
    if (isCaptureProcessing) {
      dispatch(addNotification({
        type: 'warning',
        title: 'Capture en cours',
        message: 'Veuillez attendre que la capture soit terminée avant de créer le courrier.',
      }));
      return;
    }

    // Empêcher la soumission si le scan est actif
    if (isScanning) {
      dispatch(addNotification({
        type: 'warning',
        title: 'Scan en cours',
        message: 'Veuillez terminer le scan avant de créer le courrier.',
      }));
      return;
    }

    if (!validateForm()) {
      return;
    }

    // Générer une référence automatique si elle n'est pas fournie
    const dataToSubmit = {
      ...formData,
      reference: formData.reference.trim() || `REF-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };

    try {
      // Créer le courrier (attendre la réponse pour obtenir l'id)
      const newCourrier = await (createMutation as any).mutateAsync(dataToSubmit);
      console.log('📄 [COURRIER CREATE] Courrier créé avec ID:', newCourrier.id);

      // Uploader les pièces sélectionnées (si présentes)
      if (attachedFiles.length > 0) {
        console.log('📎 [COURRIER CREATE] Upload de', attachedFiles.length, 'pièces jointes');

        const uploadPromises = attachedFiles.map(async (f, index) => {
          const fileObj: File | undefined =
            f instanceof File ? f :
              (typeof f?.file === 'object' ? (f.file as File) : undefined);

          if (!fileObj) {
            console.warn(`⚠️ [COURRIER CREATE] Fichier ${index} invalide:`, f);
            return null;
          }

          try {
            console.log(`📤 [COURRIER CREATE] Upload du fichier ${index}:`, fileObj.name);
            const uploadedPiece = await piecesService.uploadPiece(newCourrier.id, fileObj);
            console.log(`✅ [COURRIER CREATE] Pièce ${index} uploadée avec succès:`, uploadedPiece.id);
            return uploadedPiece;
          } catch (err: any) {
            console.error(`❌ [COURRIER CREATE] Erreur upload pièce ${index}:`, err);
            // Afficher une notification pour cette pièce spécifique
            dispatch(addNotification({
              type: 'warning',
              title: 'Pièce jointe non uploadée',
              message: `Le fichier "${fileObj.name}" n'a pas pu être attaché au courrier.`,
            }));
            return null;
          }
        });

        // Attendre que tous les uploads soient terminés
        const uploadResults = await Promise.allSettled(uploadPromises);
        const successfulUploads = uploadResults.filter(r => r.status === 'fulfilled' && r.value !== null).length;

        console.log(`📊 [COURRIER CREATE] Upload terminé: ${successfulUploads}/${attachedFiles.length} pièces jointes uploadées`);

        // Notification sur le résultat des uploads
        if (successfulUploads < attachedFiles.length) {
          dispatch(addNotification({
            type: 'warning',
            title: 'Upload partiel',
            message: `${successfulUploads} sur ${attachedFiles.length} pièces jointes ont été uploadées avec succès.`,
          }));
        } else {
          dispatch(addNotification({
            type: 'success',
            title: 'Pièces jointes uploadées',
            message: `Toutes les pièces jointes (${successfulUploads}) ont été uploadées avec succès.`,
          }));
        }
      }

      // Invalider les queries pour rafraîchir les données
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.statistics() });
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.detail(newCourrier.id) });

      // Notification de succès
      dispatch(addNotification({
        type: 'success',
        title: 'Courrier créé',
        message: `Le courrier "${newCourrier.subject}" a été créé avec succès.`,
      }));

      // Rediriger vers la page de détail
      navigate(`/courriers/${newCourrier.id}`);
    } catch (error: any) {
      console.error('Erreur lors de la création du courrier:', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Erreur',
        message: error?.message || 'Erreur lors de la création du courrier',
      }));
    }
  };

  const handleInputChange = (field: keyof CreateCourrierRequest) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Nettoyer l'erreur du champ
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCancel = () => {
    navigate('/courriers');
  };

  const handleFileAttached = async (file: File) => {
    try {
      const preparedFile = file.type.startsWith('image/')
        ? await fileService.compressImage(file, 1920, 0.8)
        : file;

      const id = `file_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const url = URL.createObjectURL(preparedFile);

      setAttachedFiles(prev => [...prev, { id, file: preparedFile, url }]);

      dispatch(addNotification({
        type: 'success',
        title: 'Fichier ajouté',
        message: `Le fichier "${preparedFile.name}" sera joint au courrier après création`,
      }));
    } catch (error) {
      console.error('Erreur préparation fichier:', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Erreur',
        message: 'Impossible de préparer le fichier',
      }));
    }
  };

  const handleFileRemove = async (index: number) => {
    const fileToRemove = attachedFiles[index];

    if (fileToRemove?.url) {
      try { URL.revokeObjectURL(fileToRemove.url); } catch { }
    }

    // Si c'est un fichier serveur, le supprimer du serveur
    if (fileToRemove && fileToRemove.serverFile && fileToRemove.filename) {
      try {
        await fileService.deleteFile(fileToRemove.filename);
      } catch (error) {
        console.error('Erreur suppression fichier serveur:', error);
        dispatch(addNotification({
          type: 'error',
          title: 'Erreur de suppression',
          message: 'Impossible de supprimer le fichier du serveur',
        }));
      }
    }

    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleScanComplete = async (file: File) => {
    setIsCaptureProcessing(true);
    try {
      const compressedFile = await fileService.compressImage(file, 1920, 0.8);
      const id = `scan_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const url = URL.createObjectURL(compressedFile);
      setAttachedFiles(prev => [...prev, { id, file: compressedFile, url }]);

      dispatch(addNotification({
        type: 'success',
        title: 'Scan ajouté',
        message: 'L\'image scannée a été ajoutée aux pièces jointes. Vous pouvez scanner d\'autres pages.',
      }));
      // Ne pas fermer la section de scan: permettre multi-scans
    } catch (error) {
      console.error('Erreur préparation scan:', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Erreur',
        message: 'Impossible de préparer l\'image scannée',
      }));
      // laisser le scanner ouvert pour réessayer
    } finally {
      setIsCaptureProcessing(false);
    }
  };

  const handleScanFinished = () => {
    console.log('📸 [COURRIER CREATE] Scan terminé par l\'utilisateur');
    setIsScanning(false);
    dispatch(addNotification({
      type: 'info',
      title: 'Scan terminé',
      message: 'Vous pouvez maintenant créer le courrier avec les documents scannés.',
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-4 sm:px-6 sm:py-6">
      <div className="w-full md:max-w-6xl md:mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                Nouveau Courrier
              </h1>
              <p className="text-sm text-gray-600">
                Créer un nouveau document dans le système
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleCancel}
              size="sm"
              className="w-full sm:w-auto"
            >
              Annuler
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-8">
            {/* Formulaire principal */}
            <div className="space-y-6">
              <Card title="Informations générales" className="-mx-4 sm:mx-0 bg-white rounded-none sm:rounded-lg shadow-sm border border-gray-200">
                <CardContent className="space-y-6 px-4 py-4 sm:p-6">
                  {/* Type de courrier */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type de courrier
                    </label>
                    <select
                      value={formData.type}
                      onChange={handleInputChange('type')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="ENTRANT">📥 Entrant</option>
                      <option value="SORTANT">📤 Sortant</option>
                    </select>
                  </div>

                  {/* Expéditeur et destinataire */}
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <OrganisationSelector
                        label="Expéditeur"
                        value={
                          organisations.find(org => org.displayName === formData.sender)?.id
                          || (formData.type === 'SORTANT' ? (user?.organisationId as number | undefined) : undefined)
                          || userDefaults?.defaultSenderId
                        }
                        onChange={(organisationId) => {
                          const organisation = organisations.find(org => org.id === organisationId);
                          setFormData(prev => ({ ...prev, sender: organisation?.displayName || '' }));
                        }}
                        placeholder="Sélectionner l'expéditeur..."
                        className="organisation-selector"
                        disabled={false}
                        organisations={organisations}
                      />

                      <OrganisationSelector
                        label="Destinataire"
                        value={
                          organisations.find(org => org.displayName === formData.recipient)?.id
                          || (formData.type === 'ENTRANT' ? (user?.organisationId as number | undefined) : undefined)
                          || userDefaults?.defaultRecipientId
                        }
                        onChange={(organisationId) => {
                          const organisation = organisations.find(org => org.id === organisationId);
                          setFormData(prev => ({ ...prev, recipient: organisation?.displayName || '' }));
                        }}
                        placeholder="Sélectionner le destinataire..."
                        className="organisation-selector"
                        disabled={false}
                        organisations={organisations}
                      />
                    </div>
                  </div>

                  {/* Sujet */}
                  <div>
                    <Input
                      label="Sujet"
                      value={formData.subject}
                      onChange={handleInputChange('subject')}
                      error={errors.subject}
                      placeholder="Objet du courrier"
                      required
                    />
                  </div>

                  {/* Priorité */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priorité
                    </label>
                    <select
                      value={formData.priority}
                      onChange={handleInputChange('priority')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="LOW">🟢 Basse</option>
                      <option value="NORMAL">🔵 Normale</option>
                      <option value="HIGH">🟠 Haute</option>
                      <option value="URGENT">🔴 Urgente</option>
                    </select>
                  </div>

                  {/* Référence */}
                  <div>
                    <Input
                      label="Référence (optionnel)"
                      value={formData.reference || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                      placeholder="Numéro de référence"
                    />
                  </div>

                  {/* Échéance */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Échéance (optionnel)
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.dueDate ? formatDateTimeForInput(new Date(formData.dueDate)) : ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          dueDate: value ? new Date(value).toISOString() : undefined
                        }));
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min={formatDateTimeForInput(new Date())}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Date et heure limite pour le traitement de ce courrier
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Section Documents */}
              <div>
                <Card title="📎 Documents joints" className="-mx-4 sm:mx-0 bg-white rounded-none sm:rounded-lg shadow-sm border border-gray-200">
                  <CardContent className="space-y-6 px-4 py-4 sm:p-6">
                    {/* Section Upload de fichiers (disponible sur tous les appareils) */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">Ajouter des documents</h3>
                          <p className="text-sm text-gray-500">Cliquez pour sélectionner ou glissez-déposez vos fichiers</p>
                        </div>
                      </div>

                      <FileUpload
                        onFileSelect={handleFileAttached}
                        multiple={true}
                        acceptedFormats={['application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp', 'image/webp']}
                        className="w-full"
                      />
                    </div>

                    {/* Section Scanner (uniquement sur mobile) */}
                    {isMobile && (
                      <div className="space-y-4 border-t pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800">Scanner un document</h3>
                            <p className="text-sm text-gray-500">Disponible uniquement sur mobile</p>
                          </div>
                          <Button
                            type="button"
                            variant="gradient"
                            size="sm"
                            onClick={() => setIsScanning(!isScanning)}
                            className="flex items-center space-x-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{isScanning ? 'Arrêter le scan' : 'Scanner'}</span>
                          </Button>
                        </div>

                        {isScanning && (
                          <div className="-mx-4 sm:mx-0 bg-gray-50 border border-gray-200 rounded-none sm:rounded-lg p-0 sm:p-4">
                            <div className="mb-3 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                              <p className="text-sm text-yellow-800 flex items-center">
                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Le scan est actif - terminez-le avant de créer le courrier
                              </p>
                            </div>

                            {detectorLoading ? (
                              <div className="flex items-center justify-center p-8">
                                <div className="text-gray-500">Chargement du scanner...</div>
                              </div>
                            ) : (
                              <CameraCaptureOpenCV
                                onCapture={handleScanComplete}
                                onCancel={() => setIsScanning(false)}
                                onScanComplete={handleScanFinished}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Galerie des documents scannés / attachés */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-md font-semibold text-gray-800">
                          Documents en attente ({attachedFiles.length})
                        </h4>
                        {isScanning && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setIsScanning(false)}
                            className="px-3 py-1.5 text-sm"
                            disabled={isCaptureProcessing}
                          >
                            {isCaptureProcessing ? (
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                <span>Traitement...</span>
                              </div>
                            ) : (
                              'Terminer le scan'
                            )}
                          </Button>
                        )}
                      </div>

                      {attachedFiles.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          Ajoutez des documents en scannant avec la caméra ou en important un fichier.
                        </p>
                      ) : (
                        <ImageGallery
                          images={attachedFiles
                            .map((it: any, idx: number) => ({
                              src: it?.url || '',
                              id: it?.id || String(idx),
                              alt: it?.file?.name || it?.name || `scan-${idx}`,
                            }))
                            .filter(img => !!img.src)}
                          onRemove={(id) => {
                            const idx = attachedFiles.findIndex((f: any, i: number) => (f?.id || String(i)) === id);
                            if (idx >= 0) handleFileRemove(idx);
                          }}
                          className="mt-2"
                        />
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Actions */}
              <Card className="-mx-4 sm:mx-0 bg-white rounded-none sm:rounded-lg shadow-sm border border-gray-200">
                <CardContent className="space-y-4 px-4 py-4 sm:p-6">
                  <Button
                    type="submit"
                    variant="gradient"
                    size="lg"
                    className="w-full h-12 text-lg"
                    isLoading={createMutation.isPending}
                    disabled={createMutation.isPending || isScanning || isCaptureProcessing}
                  >
                    {createMutation.isPending ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Création...</span>
                      </div>
                    ) : isScanning || isCaptureProcessing ? (
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <span>Terminez le scan d'abord</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span>Créer le courrier</span>
                      </div>
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12"
                    onClick={handleCancel}
                    disabled={createMutation.isPending}
                  >
                    Annuler
                  </Button>
                </CardContent>
              </Card>
            </div>

          </div>
        </form>
      </div>
    </div>
  );
}